import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import GlassCard from '../components/GlassCard';
import FuturisticButton from '../components/FuturisticButton';

const { FiUpload, FiShield, FiZap, FiActivity, FiFileText, FiUsers, FiCheck, FiLayers } = FiIcons;

const Home = () => {
  const features = [
    {
      icon: FiUpload,
      title: 'Matrix Upload',
      description: 'Advanced file processing with multi-format support and intelligent token management, compliant with Australian privacy standards'
    },
    {
      icon: FiZap,
      title: 'Neural AI Analysis',
      description: 'Next-generation AI translates complex medical terminology into crystal-clear explanations for Australian patients'
    },
    {
      icon: FiShield,
      title: 'Australian Privacy Compliant',
      description: 'Zero-storage processing with complete compliance to Australian Privacy Principles and health information standards'
    }
  ];

  const benefits = [
    'Decode complex laboratory results with matrix precision',
    'Understand Australian medical terminology and reference ranges',
    'Generate intelligent questions for your GP or specialist',
    'Reduce anxiety through clear explanations',
    'Share insights confidently with family members',
    'Process large documents in optimised segments',
    'Emergency contact integration (000, healthdirect)',
    'Compliant with Australian health regulations'
  ];

  return (
    <div className="min-h-screen relative overflow-x-hidden">
      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 md:pt-32 lg:pt-40 pb-8 sm:pb-12 md:pb-16 lg:pb-20 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative mb-4 sm:mb-6 md:mb-8">
                <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-7xl font-bold mb-3 sm:mb-4 md:mb-6 terminal-text leading-tight px-2">
                  <span className="block bg-gradient-to-r from-green-400 via-green-300 to-white bg-clip-text text-transparent">
                    Australian Medical
                  </span>
                  <span className="block bg-gradient-to-r from-white via-green-300 to-red-400 bg-clip-text text-transparent">
                    Intelligence
                  </span>
                </h1>
                {/* Glowing orb effect */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 sm:w-48 md:w-64 lg:w-96 h-32 sm:h-48 md:h-64 lg:h-96 bg-gradient-to-r from-green-400/10 to-green-500/10 rounded-full blur-3xl -z-10" />
              </div>
              
              <p className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl text-green-200 mb-6 sm:mb-8 md:mb-12 max-w-4xl mx-auto leading-relaxed px-4">
                Revolutionary AI-powered platform that transforms complex medical reports into{' '}
                <span className="text-white font-semibold">crystal-clear insights</span> for Australian patients, fully compliant with Australian Privacy Principles and health regulations.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex flex-col gap-3 sm:gap-4 justify-center items-center mb-6 sm:mb-8 md:mb-12 lg:mb-16 max-w-lg mx-auto px-4"
            >
              <FuturisticButton
                as={Link}
                to="/upload"
                size="xl"
                icon={FiActivity}
                className="group w-full"
              >
                <span className="text-sm sm:text-base md:text-lg">Begin Matrix Analysis</span>
                <div className="absolute inset-0 bg-gradient-to-r from-green-400/20 to-green-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </FuturisticButton>
            </motion.div>

            {/* Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 lg:gap-8 max-w-4xl mx-auto px-4"
            >
              {[
                { number: '128K', label: 'Token Capacity' },
                { number: '99.9%', label: 'Accuracy Rate' },
                { number: '10MB', label: 'Max File Size' },
                { number: '0ms', label: 'Data Retention' }
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-green-400 mb-1 terminal-text">
                    {stat.number}
                  </div>
                  <div className="text-xs sm:text-sm text-white">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Australian Compliance Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mt-4 sm:mt-6 md:mt-8 px-4"
            >
              <GlassCard className="inline-block px-3 sm:px-4 md:px-6 py-2 sm:py-3">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiShield} className="h-4 w-4 sm:h-5 sm:w-5 text-green-400" />
                  <span className="text-green-300 text-xs sm:text-sm font-semibold">Australian Privacy Act Compliant</span>
                  <span className="text-white text-xs sm:text-sm">🇦🇺</span>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12 md:mb-16 lg:mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4 md:mb-6 terminal-text px-2">
                Advanced Neural{' '}
                <span className="bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
                  Capabilities
                </span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-green-200 max-w-3xl mx-auto px-2">
                Powered by cutting-edge AI technology, designed for Australian healthcare standards
              </p>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
              >
                <GlassCard className="p-4 sm:p-6 md:p-8 h-full group">
                  <div className="relative mb-4 sm:mb-6">
                    <div className="bg-gradient-to-r from-green-400/20 to-green-500/20 w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <SafeIcon icon={feature.icon} className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-green-400" />
                    </div>
                    <div className="absolute -inset-2 bg-gradient-to-r from-green-400/10 to-green-500/10 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  </div>
                  <h3 className="text-base sm:text-lg md:text-xl font-semibold text-white mb-2 sm:mb-3 md:mb-4 terminal-text">
                    {feature.title}
                  </h3>
                  <p className="text-sm sm:text-base text-green-200 leading-relaxed">
                    {feature.description}
                  </p>
                  {/* Card scan line */}
                  <motion.div
                    className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-green-400/60 to-transparent"
                    animate={{ x: ['-100%', '100%'] }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 4 + index, ease: 'linear' }}
                  />
                </GlassCard>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 xl:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-4 sm:mb-6 md:mb-8 terminal-text">
                Transform Your{' '}
                <span className="bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
                  Australian Health Journey
                </span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-green-200 mb-4 sm:mb-6 md:mb-8 leading-relaxed">
                Break through medical complexity with AI-powered insights designed for Australian patients, making your health data accessible and understandable while maintaining strict privacy compliance.
              </p>
              <div className="space-y-2 sm:space-y-3 md:space-y-4">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <div className="relative">
                      <SafeIcon icon={FiCheck} className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <div className="absolute -inset-1 bg-green-400/30 rounded-full blur animate-pulse" />
                    </div>
                    <span className="text-sm sm:text-base text-white">{benefit}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <GlassCard className="p-4 sm:p-6 md:p-8 text-center" glow>
                <div className="relative mb-4 sm:mb-6">
                  <SafeIcon icon={FiActivity} className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 text-green-400 mx-auto" />
                  <div className="absolute -inset-4 bg-gradient-to-r from-green-400/20 to-green-500/20 rounded-full blur-xl animate-pulse" />
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2 sm:mb-3 md:mb-4 terminal-text">
                  Australian Healthcare Intelligence
                </h3>
                <p className="text-sm sm:text-base text-green-200 mb-4 sm:mb-6 leading-relaxed">
                  Advanced AI specifically calibrated for Australian medical standards, reference ranges, and healthcare terminology, ensuring accurate interpretations for Australian patients.
                </p>
                <div className="grid grid-cols-2 gap-3 sm:gap-4 text-sm">
                  <div className="text-center">
                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-green-400 mb-1">🇦🇺</div>
                    <div className="text-white text-xs sm:text-sm">AU Compliant</div>
                  </div>
                  <div className="text-center">
                    <div className="text-lg sm:text-xl md:text-2xl font-bold text-red-400 mb-1">🔒</div>
                    <div className="text-white text-xs sm:text-sm">Zero Storage</div>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Emergency Contacts Section */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-4 sm:p-6 md:p-8 lg:p-12">
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 sm:mb-6 terminal-text">
                Australian Emergency Contacts
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-4 sm:mb-6 md:mb-8">
                <GlassCard className="p-3 sm:p-4 border border-red-400/30">
                  <div className="text-lg sm:text-xl md:text-2xl font-bold text-red-400">000</div>
                  <div className="text-xs sm:text-sm text-white">Emergency Services</div>
                </GlassCard>
                <GlassCard className="p-3 sm:p-4 border border-yellow-400/30">
                  <div className="text-sm sm:text-base md:text-lg font-bold text-yellow-400">13 11 14</div>
                  <div className="text-xs sm:text-sm text-white">Lifeline Australia</div>
                </GlassCard>
                <GlassCard className="p-3 sm:p-4 border border-blue-400/30">
                  <div className="text-xs sm:text-sm md:text-base font-bold text-blue-400">1800 022 222</div>
                  <div className="text-xs sm:text-sm text-white">healthdirect</div>
                </GlassCard>
              </div>
              <p className="text-sm sm:text-base text-green-200">
                Quick access to Australian emergency and health support services when you need them most
              </p>
            </GlassCard>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-8 sm:py-12 md:py-16 lg:py-20 relative z-10">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-4 sm:p-6 md:p-8 lg:p-12" glow>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white mb-3 sm:mb-4 md:mb-6 terminal-text">
                Ready to Understand Your{' '}
                <span className="bg-gradient-to-r from-green-400 to-green-300 bg-clip-text text-transparent">
                  Australian Health Records?
                </span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-green-200 mb-4 sm:mb-6 md:mb-8 max-w-2xl mx-auto">
                Join thousands of Australian patients gaining clarity about their medical reports through AI-powered analysis, fully compliant with Australian privacy laws.
              </p>
              <div className="max-w-sm mx-auto">
                <FuturisticButton
                  as={Link}
                  to="/upload"
                  size="xl"
                  icon={FiFileText}
                  className="group relative w-full"
                >
                  <span className="text-sm sm:text-base md:text-lg">Begin Matrix Analysis</span>
                  <div className="absolute -inset-1 bg-gradient-to-r from-green-400/20 to-green-500/20 rounded-xl blur opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </FuturisticButton>
              </div>
            </GlassCard>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;